import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { recordingQueryOptions } from '@/entities/recording'
import { downloadFilename, downloadFromUrl, filenameFromUrl } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'

// Скачивание записи: fetch → blob (presigned-URL живёт на s3.<domain>, где
// атрибут download кросс-доменно игнорируется). Фолбэк — открыть в новой вкладке.
async function downloadRecording(url: string, filename: string) {
  try {
    await downloadFromUrl(url, filename)
  } catch {
    toast.error('Не удалось скачать файл, открываю в новой вкладке')
    window.open(url, '_blank', 'noopener')
  }
}

interface RecordingSectionProps {
  meetingId: string
  title: string
  dateIso: string
}

// Запись прошедшей встречи. Одинакова для сотрудника и для заказчика: у
// заказчика запись открыта ровно тогда, когда открыта сама встреча.
export function RecordingSection({ meetingId, title, dateIso }: RecordingSectionProps) {
  const { data, isLoading, isError } = useQuery(recordingQueryOptions(meetingId))
  // Скачивание fetch→blob занимает время на больших файлах — кнопка честно
  // показывает процесс и не даёт запустить параллельное второе.
  const [isDownloading, setIsDownloading] = useState(false)

  if (isLoading) return <Spinner label="Загрузка записи" />
  if (isError || !data) return <p className="text-text-tertiary">Запись недоступна.</p>

  // Скачивается только сводный аудиофайл (room-composite). Пер-трековые
  // дорожки — сырьё транскрипции, наружу не отдаются.
  const compositeUrl = data.composite?.url
  if (compositeUrl) {
    // Расширение — из ключа объекта в presigned-URL, имя — человекочитаемое.
    const ext = filenameFromUrl(compositeUrl, 'запись.ogg').split('.').pop() ?? 'ogg'
    const filename = downloadFilename(title, dateIso, 'запись голоса', ext)
    const url = compositeUrl

    async function download() {
      if (isDownloading) return
      setIsDownloading(true)
      try {
        await downloadRecording(url, filename)
      } finally {
        setIsDownloading(false)
      }
    }

    return (
      // Текстовая кнопка без рамки: бледный бордер читался как смещение,
      // левый край — по общей направляющей с заголовком секции.
      <button
        type="button"
        disabled={isDownloading}
        onClick={() => void download()}
        className="inline-flex items-center gap-2 self-start font-medium font-mono text-text-secondary text-xs uppercase tracking-wide transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:pointer-events-none disabled:opacity-40"
      >
        {isDownloading ? (
          <>
            <Spinner label="Скачивание" />
            Скачивание…
          </>
        ) : (
          <>
            <Icon name="record" size={16} />
            Скачать запись
          </>
        )}
      </button>
    )
  }
  return (
    <p className="text-text-tertiary">
      {data.state === 'recording' ? 'Запись ещё идёт.' : 'Сводный файл записи недоступен.'}
    </p>
  )
}
